import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError, finalize, map} from 'rxjs/operators';

import {IsEmptyString} from '../../defines/common.func';
import {EXAMPLE_PAGE_SIZE} from '../../defines/desktop.const';
import {EXAMPLE_API_URL, EXAMPLE_DETAIL_API_URL, EXAMPLE_TREE_API_URL, PAGE} from '../../defines/protocol';
import {SearchParam} from '../../defines/client.const';
import {Example} from '../../models/example.model';
import {ExampleCatTree} from '../../models/example-tree.model';
import {generateUrlWithQueryParams, getQueryParamsFromUrl, NormalizeSearchKeyword} from '../../defines/client.func';
import {PageLoader} from '../../interfaces/page-loader';
import {ApiService} from '../api/api.service';

@Injectable({
  providedIn: 'root'
})
export class ExampleService implements PageLoader<Example, string> {
  protected urlApi = EXAMPLE_API_URL;

  protected curPage: number | null = null;
  protected totalCount: number | null = null;
  protected listExample: Array<Example> | null = null;
  protected mapExample: Map<string, Example> | null = null;
  protected nextUrl: string | null = null;
  protected prevUrl: string | null = null;
  protected load$: Observable<Array<Example>> | null = null;
  protected loadDetail$: Observable<Example> | null = null;
  protected loadTree$: Observable<Array<ExampleCatTree>> | null = null;
  protected listTree: Array<ExampleCatTree> | null = null;


  constructor(
    protected api: ApiService,
  ) { }


  /******************************************************************************
   * PageLoader<Example> 구현부
   *****************************************************************************/
  public getList(): Array<Example> | null {
    return this.listExample;
  }
  public getCurPage(): number | null {
    return this.curPage;
  }
  public getTotalCount(): number | null {
    return this.totalCount;
  }
  public IsFirstPage(): boolean {
    return IsEmptyString(this.prevUrl);
  }
  public IsLastPage(): boolean {
    return IsEmptyString(this.nextUrl);
  }
  public IsLoading(): boolean {
    return this.load$ !== null;
  }
  public getById(id: string): Example | null {
    return this.mapExample?.get(id) ?? null;
  }

  public ResetList(param?: SearchParam): Observable<number> {
    this.listExample = null;
    this.mapExample = null;
    this.curPage = 0;
    this.totalCount = null;
    this.nextUrl = this.urlApi;
    this.prevUrl = null;

    return new Observable<number>(observer => {

      if (param) {
        const keyword = NormalizeSearchKeyword(param.keyword);
        this.nextUrl = generateUrlWithQueryParams(
          this.urlApi,
          {
            page_size: EXAMPLE_PAGE_SIZE,
            ...('page' in param ? {page: param.page} : {}),
            ...('start' in param ? {start: param.start} : {}),
            ...('pp' in param ? {pp: param.pp} : ('postposition' in param ? {pp: param.postposition} : {})),
            ...('example_cat' in param ? {cat: param.example_cat} : {}),
            ...(keyword ? {search: keyword} : {}),
        });
      }
      else {
        this.nextUrl = this.urlApi;
      }

      console.log('ExampleService::ResetList() nextUrl =', this.nextUrl);

      observer.next(1);
      observer.complete();
    });
  }

  public GetNextList(): Observable<Array<Example>> {

    if (this.nextUrl === null) {
      return throwError({error: 'No more next pages in ExampleList'});
    }

    const nextUrl = this.nextUrl;
    return this.getUrlList(nextUrl).pipe(
      finalize(
        () => {
          const param = getQueryParamsFromUrl(nextUrl);
          this.curPage = 'page' in param ? parseInt(param['page'], 10): 1;
        }
      )
    );
  }

  public GetPreList(): Observable<Array<Example>> {

    if (this.prevUrl === null) {
      return throwError({error: 'No more pre pages in ExampleList'});
    }

    const prevUrl = this.prevUrl;
    return this.getUrlList(prevUrl).pipe(
      finalize(
        () => {
          const param = getQueryParamsFromUrl(prevUrl)
          this.curPage = 'page' in param ? parseInt(param['page'], 10) : 1;
        }
      )
    );
  }

  protected getUrlList(url: string): Observable<Array<Example>> {

    if (this.load$) {
      return this.load$;
    }

    return this.load$ = this.api.Get(url).pipe(
      catchError(err => {
        if (err?.status === 404) {
          this.totalCount = 0;
        }
        throw err;
      }),
      finalize(() => {
        this.load$ = null;
      }),
      map((data: PAGE) => {
        console.log('ExampleService::getUrlList.api.Get data =', data);

        if (this.totalCount === null) {
          this.totalCount = data.count;
          this.listExample = new Array<Example>();
          this.mapExample = new Map<string, Example>();
        }
        else if (this.totalCount !== data.count) {
          console.error('[error]' + this.urlApi, data);
        }

        data.results.forEach(value => {
          if (this.listExample === null || this.mapExample === null) {
            throw new Error('ExampleService::getUrlList() listExample or mapExample is null');
          }
          const example = new Example(value);
          this.listExample.push(example);
          this.mapExample.set(example.id, example);
        });

        this.nextUrl = data.next ?? null;
        this.prevUrl = data.previous ?? null;

        return this.listExample!;
      })
    );
  }


  /******************************************************************************
   * 트리 구조(범주 + 조사) 획득
   *****************************************************************************/
  public getTree(): Array<ExampleCatTree> | null {
    return this.listTree;
  }

  public GetTree(): Observable<Array<ExampleCatTree>> {
    const url = EXAMPLE_TREE_API_URL;
    console.log('ExampleService::GetTree() url =', url);

    if (this.loadTree$) {
      return this.loadTree$;
    }

    return this.loadTree$ = this.api.Get(url).pipe(
      finalize(() => this.loadTree$ = null),
      map((data: PAGE) => {
        this.listTree = new Array<ExampleCatTree>();
        data.results.forEach(value => {
          this.listTree?.push(new ExampleCatTree(value));
        });
        console.log('ExampleService::GetTree data =', this.listTree);
        return this.listTree;
      })
    );
  }

  /******************************************************************************
   * 상세 예문 정보 획득
   * TODO: 로딩 중인 것을 처리. (예외 상황 처리 추가해야 함)
   *****************************************************************************/
  public GetDetail(exampleId: string): Observable<Example> {
    const url = EXAMPLE_DETAIL_API_URL + exampleId + '/';
    console.log('ExampleService::GetDetail() url =', url);

    return this.loadDetail$ = this.api.Get(url).pipe(
      finalize(() => this.loadDetail$ = null),
      map(value => {
        const out = new Example(value);
        console.log('ExampleService::GetDetail data = ', value, out);
        return out;
      })
    );
  }

}
